import {IHierarchyNodeProvider, INode, INodeLogicalKey} from "../PA.MD.Core/PA.MD.IF.Hierarchy";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {
    esWB_Entity,
    esWB_Finished_Product,
    esWB_Finished_Product_Relationship,
    esWB_Finished_Product_SalesArea,
    esWB_Finished_Product_Tag_Relationship, esWB_MasterData_Set,
    esWB_Product_Attribute,
    esWB_Product_BOM,
    esWB_Product_Group,
    esWB_Product_Group_Attribute, esWB_Product_Group_External_Relationship, esWB_Product_Group_LogicalKey,
    esWB_Product_Group_Relationship,
    esWB_Product_Group_SalesArea, esWB_Product_Group_Tag_Relationship, esWB_Product_Group_UOM,
    esWB_Product_UOM
} from "../PA.Core/PAdbSchema";
import * as _ from "lodash"
import {ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {
    WB_EntityRow,
    WB_FinishedProductRow,
    WB_MasterDataSetRow, WB_ProductGroupLogicalKeyRow,
    WB_ProductGroupRow
} from "../PA.Core/PAdbSchemaRows";
import {ProductNode} from "./ProductNode";
import {ProductGroupNode} from "./ProductGroupNode";
import {HierarchyUtils} from "../PA.Core/Basic/HierarchyUtils";
import * as AsyncLock from 'async-lock';
import {TextUtils} from "../PA.Core/Basic/Text";
import {HierarchyService} from "./HierarchyService";

const knownMappers = {
    [esWB_Finished_Product]: esWB_Finished_Product,
    [esWB_Product_Group]: esWB_Product_Group
}

export class ProductNodeProvider
    implements IHierarchyNodeProvider {
    private lkNodeLock:AsyncLock

    private dbContext: PAdbContext
    private wbMDSetId: number
    private masterDataSetRow: WB_MasterDataSetRow
    private dctByWbId: { [mapperId: string]: { [wbId: number]: INode } }
    private dctByKeySetId: { [mapperId: string]: { [keySetId: string]: { [Id: string]: INode } } }
    private dctByLKHash: { [hashKey: string]: INode }

    private loaded: boolean
    private arrEntityTypes: WB_EntityRow[]


    constructor(dbContext: PAdbContext, wbMDSetId: number) {
        this.dbContext = dbContext
        this.wbMDSetId = wbMDSetId
        this.dctByWbId = {[esWB_Finished_Product]: {}, [esWB_Product_Group]: {}}
        this.dctByKeySetId = {[esWB_Finished_Product]: {}, [esWB_Product_Group]: {}}
        this.dctByLKHash = {}

        this.loaded = false
        this.lkNodeLock = new AsyncLock({})
        this.arrEntityTypes = []

    }

    get bLoaded(): boolean { return this.loaded }

    async OnLoadComplete(): Promise<void> {
        await this.checkLoaded()
    }

    private async checkLoaded(){
        if(this.loaded)
            return

        this.masterDataSetRow = <WB_MasterDataSetRow> await this.dbContext.find(esWB_MasterData_Set, this.wbMDSetId)


        this.arrEntityTypes = <WB_EntityRow[]> await this.dbContext.findAll(esWB_Entity, {
            where: {
                WB_MDSET_ID: this.wbMDSetId,
                ENTITY_DIMENSION: "Product",
                DELETED_IND: {neq: '1'}
            }
        })

        this.loaded = true

    }

    // Async, read from Source as needed, loads into cache
    //-- Find one
    async findNodeByWbId(mapperName: string, wbId: number): Promise<INode> {
        await this.checkLoaded()

        let arr = await this.findNodesByWbIds(mapperName, [wbId])
        if (arr.length)
            return arr[0]

        return undefined
    }

    async findNodeByKeySetId(mapperName: string, sKeySetId: string, sId: string): Promise<INode> {
        await this.checkLoaded()

        let arr = await this.findNodesByKeySetIds(mapperName, sKeySetId, [sId])
        if (arr.length)
            return arr[0]

        return undefined
    }

    //-- Find multiple
    async findNodesByWbIds(mapperName: string, wbIds: number[]): Promise<INode[]> {
        await this.checkLoaded()

        if (!(mapperName in knownMappers))
            return []

        let searchIds: number[] = []
        let rv: INode[] = []
        _.forEach(wbIds, (wbId) => {
            let nd = this.findNodeByWbIdSync(mapperName, wbId)
            if (_.isEmpty(nd))
                searchIds.push(wbId)
            else
                rv.push(nd)
        })

        if (_.isEmpty(searchIds))
            return rv

        let rr = this.getRecordResolver()
        let rows: WB_FinishedProductRow[] | WB_ProductGroupRow[] = <WB_FinishedProductRow[] | WB_ProductGroupRow[]>await rr.findAllWithRelationships(mapperName, searchIds)
        for (let aRow of rows) {
            let cn: INode = undefined
            if (mapperName == esWB_Finished_Product)
                cn = new ProductNode(aRow as WB_FinishedProductRow, this)
            else
                cn = new ProductGroupNode(aRow as WB_ProductGroupRow, this)
            this.addNodeToCache(cn)
            rv.push(cn)
        }

        return rv
    }
    async findNodesByKeySetIds(mapperName: string, sKeySetId: string, arrIds: string[]): Promise<INode[]> {
        await this.checkLoaded()

        if (!(mapperName in knownMappers))
            return []

        let searchIds: string[] = []
        let rv: INode[] = []
        _.forEach(arrIds, (sId) => {
            let nd = this.findNodeByKeySetIdSync(mapperName, sKeySetId, sId)
            if (_.isEmpty(nd))
                searchIds.push(sId)
            else
                rv.push(nd)
        })

        if (_.isEmpty(searchIds))
            return rv

        let rr = this.getRecordResolver()
        let queryCnd
        if (mapperName == esWB_Finished_Product) {
            queryCnd = {
                WB_MDSET_ID: this.wbMDSetId,
                PRODUCT_SET_ID: sKeySetId,
                PRODUCT_ID: {inq: searchIds},
                DELETED_IND: {neq: '1'}
            }
        } else {
            queryCnd = {
                WB_MDSET_ID: this.wbMDSetId,
                PRODUCT_GROUP_SET_ID: sKeySetId,
                PRODUCT_GROUP_ID: {inq: searchIds},
                DELETED_IND: {neq: '1'}
            }
        }

        let rows: WB_FinishedProductRow[] | WB_ProductGroupRow[] = <WB_FinishedProductRow[] | WB_ProductGroupRow[]>await rr.queryAllWithRelationships(mapperName, {
            where: queryCnd
        })
        for (let aRow of rows) {
            let cn: INode = undefined
            if (mapperName == esWB_Finished_Product)
                cn = new ProductNode(aRow as WB_FinishedProductRow, this)
            else
                cn = new ProductGroupNode((aRow as WB_ProductGroupRow), this)
            this.addNodeToCache(cn)
            rv.push(cn)
        }

        return rv

    }

    // Sync, pull from cache only
    findNodeByWbIdSync(mapperName: string, wbId: number): INode {
        if (!(mapperName in knownMappers))
            return undefined

        let dct = this.dctByWbId[mapperName] || {}
        if (wbId in dct)
            return dct[wbId]

        return undefined
    }

    findNodeByKeySetIdSync(mapperName: string, keySetId: string, id: string): INode {
        if (!(mapperName in knownMappers))
            return undefined

        let dctL1 = this.dctByKeySetId[mapperName] || {}
        if (!(keySetId in dctL1))
            return undefined

        let dctL2 = dctL1[keySetId]
        if (!(id in dctL2))
            return undefined

        return dctL2[id]
    }
    findNodeByLogicalKeySync(logicalKey: INodeLogicalKey): INode {

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)

        if(_.isEmpty(sLKHash))
            return undefined
        if(sLKHash in this.dctByLKHash)
            return this.dctByLKHash[sLKHash]

        return undefined
    }

    async findNodeByLogicalKey(logicalKey: INodeLogicalKey): Promise<INode> {
        await this.checkLoaded()

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)
        if(_.isEmpty(sLKHash))
            return undefined

        return await this.lkNodeLock.acquire(sLKHash, (done) => {

            // A. Check in Cache
            if(sLKHash in this.dctByLKHash) {
                done(undefined, this.dctByLKHash[sLKHash])
                return

            }

            (async () => {

                // B. Check in DB
                let hs = new HierarchyService()
                let info = await hs.getNodeWbIdByLKNode(this.wbMDSetId, "Product", logicalKey)

                let wbId_found = info.wbIds[0] || 0
                if(info.wbIds?.length == 1){
                    if(wbId_found > 0){
                        let nd = await this.findNodeByWbId(esWB_Product_Group, wbId_found)
                        done(undefined, nd)
                        return
                    }
                } else if(info.dupError){
                    done(new Error(`Duplicate Hierarchy Node Err wbIds: ${info.wbIds.join(", ")}`), undefined)
                    return
                }


                done(undefined, undefined)

            })();


        })

    }

    // async for MD Generated Hierarchy
    // A. Check in Cache
    // B. Check in DB
    // C. Create if not found
    async findOrCreateLogicalKeyNode(logicalKey: INodeLogicalKey, options?: {sCreateId?: string, sCreateName?: string, dbContext?: PAdbContext}): Promise<INode> {
        await this.checkLoaded()

        let sLKHash = HierarchyUtils.getLogicKeyHash(logicalKey)
        if(_.isEmpty(sLKHash))
            return undefined

        let opts = options || {}

        return await this.lkNodeLock.acquire(sLKHash, (done) => {

            // A. Check in Cache
            if(sLKHash in this.dctByLKHash) {
                done(undefined, this.dctByLKHash[sLKHash])
                return

            }

            (async () => {

                // B. Check in DB
                let hs = new HierarchyService()
                let info = await hs.getNodeWbIdByLKNode(this.wbMDSetId, "Product", logicalKey)

                let wbId_found = info.wbIds[0] || 0
                if(info.wbIds?.length == 1){
                    if(wbId_found > 0){
                        let nd = await this.findNodeByWbId(esWB_Product_Group, wbId_found)
                        done(undefined, nd)
                        return
                    }
                } else if(info.dupError){
                    done(new Error(`Duplicate Hierarchy Node Err wbIds: ${info.wbIds.join(", ")}`), undefined)
                    return
                }

                // C. Create Node if not found
                let setId = 'HNode'
                let aHR = <WB_ProductGroupRow>await this.dbContext.createRecordWithKey(esWB_Product_Group)
                let wbId = aHR.cif.WB_PRDGRP_ID
                let id_ui = TextUtils.padStart(wbId.toString(36), 12, '0').toUpperCase()
                aHR.cif.PRODUCT_GROUP_ID =  opts.sCreateId || ('HN-' + id_ui)
                aHR.cif.PRODUCT_GROUP_NAME = opts.sCreateName || id_ui
                aHR.cif.WB_MDSET_ID = this.wbMDSetId
                aHR.cif.PRODUCT_GROUP_SET_ID = setId
                aHR.cif.HIER_NODE_IND = '1'
                this.dbContext.add(esWB_Product_Group, aHR)
                for (let aKey in logicalKey) {
                    let eLK = logicalKey[aKey]
                    let lkComp = <WB_ProductGroupLogicalKeyRow>await this.dbContext.createRecordWithKey(esWB_Product_Group_LogicalKey)
                    lkComp.cif.WB_PRDGRP_ID = wbId
                    lkComp.WB_ProductGroupRow = aHR
                    lkComp.cif.KEY_ID = aKey
                    lkComp.cif.DATATYPE = eLK.dataType
                    lkComp.cif.VALUE_NUMBER = eLK.valueNumber || 0
                    lkComp.cif.VALUE_STRING = eLK.valueString || ""
                    lkComp.cif.VALUE_WB_ENTITY_ID = eLK.valueWbEntityId || 0
                    lkComp.cif.VALUE_WB_ID = eLK.valueWbId || 0
                    lkComp.cif.VALUE_WB_ATTRIBUTEM_ID = eLK.valueWbAttributeMId || 0
                    this.dbContext.add(esWB_Product_Group_LogicalKey, lkComp)
                }

                // create INode
                let cn = new ProductGroupNode(aHR, this)

                // Saving Node
                let dbContext = opts.dbContext || this.dbContext
                dbContext.BatchStart()
                await dbContext.SaveBatch(esWB_Product_Group, aHR)
                await dbContext.SaveBatch(esWB_Product_Group_LogicalKey, aHR.WB_ProductGroupLogicalKeyRows)
                await dbContext.BatchComplete()

                this.addNodeToCache(cn)

                done(undefined, cn)

            })();


        })

    }

    get entityTypes(): WB_EntityRow[] {
        return this.arrEntityTypes
    }

    // Private Functions
    private addNodeToCache(aNode: INode): void {
        if (_.isEmpty(aNode))
            return

        let mapperId = aNode.mapperName
        let wbId = aNode.wbId
        let keySetId = aNode.key_set
        let Id = aNode.id_ui

        // Always add to wbId dictionary
        if (!(mapperId in this.dctByWbId))
            this.dctByWbId[mapperId] = {}
        if (!(wbId in this.dctByWbId[mapperId]))
            this.dctByWbId[mapperId][wbId] = aNode

        if (aNode.bUseLogicalKey) {
            let sLKHash = HierarchyUtils.getLogicKeyHash(aNode.logicalKey)
            this.dctByLKHash[sLKHash] = aNode
        } else {
            if (!(mapperId in this.dctByKeySetId))
                this.dctByKeySetId[mapperId] = {}
            if (!(keySetId in this.dctByKeySetId[mapperId]))
                this.dctByKeySetId[mapperId][keySetId] = {}
            if (!(wbId in this.dctByKeySetId[mapperId][keySetId]))
                this.dctByKeySetId[mapperId][keySetId][Id] = aNode
        }
    }

    private getRecordResolver(): ESRecordResolver {
        let rr = new ESRecordResolver(this.dbContext, {
            standardConditions: {
                [esWB_Finished_Product]: {"DELETED_IND": {"notIn": ['1']}},
                [esWB_Product_Group]: {"DELETED_IND": {"notIn": ['1']}},
            },
            // We only want: esWB_Prd_LogicalKey
            dctExcludeHasManyNamedRelations: {
                [esWB_Finished_Product]: [
                    esWB_Finished_Product_SalesArea,
                    esWB_Finished_Product_Relationship,
                    esWB_Product_BOM,
                    esWB_Product_UOM,
                    esWB_Finished_Product_Tag_Relationship,
                    esWB_Product_Attribute
                ],
                [esWB_Product_Group]: [
                    esWB_Product_Group_SalesArea,
                    esWB_Product_Group_Relationship,
                    esWB_Product_Group_Attribute,
                    esWB_Finished_Product_Relationship,
                    esWB_Product_Group_External_Relationship,
                    esWB_Product_Group_Tag_Relationship,
                    esWB_Product_Group_UOM,
                    esWB_Product_Attribute
                ]
            },
            dctIncludeBelongsToNamedRelations: {}
        })

        return rr
    }

}